/**
 * Metric card style tokens (Figma Style Guide — Helo Bolt).
 *
 * Figma style token → Chakra theme path (preset parent shown for inheritance):
 * | Figma style token                              | Chakra path                                      | Preset parent              |
 * |------------------------------------------------|--------------------------------------------------|----------------------------|
 * | component/metric-card/metric-card-label-color  | component.metricCard.dark.labelColor             | presets.neutral.0          |
 * | component/metric-card/metric-card-value-color  | component.metricCard.dark.valueColor             | presets.turquoise.30       |
 * | component/metric-card/metric-card-border-color | component.metricCard.dark.borderColor          | presets.overlay.turquoise50 |
 * | component/metric-card/metric-card-background   | component.metricCard.dark.background             | presets.overlay.white02    |
 * | component/metric-card/metric-card-separator-color | component.metricCard.dark.separatorColor      | presets.overlay.turquoise50 |
 */
export const metricCardLayout = {
  borderRadius: '8px',
  borderWidth: '1px',
  backdropBlur: '4px',
  containerPaddingX: '16px',
  containerPaddingTop: '24px',
  containerPaddingBottom: '20px',
  containerMinHeight: '112px',
  cardMinWidth: '170px',
  cardPaddingLeft: '32px',
  cardPaddingRight: '8px',
  contentGap: '8px',
  separatorWidth: '1px',
} as const

export const metricCardTypography = {
  dark: {
    label: { fontSize: '14px', fontWeight: 600, lineHeight: '1' },
    value: { fontSize: '28px', fontWeight: 600, lineHeight: '1.2' },
  },
  light: {
    label: { fontSize: '14px', fontWeight: 600, lineHeight: '1' },
    value: { fontSize: '36px', fontWeight: 700, lineHeight: '1.2' },
  },
} as const

export type MetricCardColorMode = keyof typeof metricCardTypography

type MetricCardColorRole =
  | 'labelColor'
  | 'valueColor'
  | 'borderColor'
  | 'background'
  | 'separatorColor'

/** Chakra `color` / `bg` / `borderColor` path on `theme.colors.component.metricCard`. */
export const componentMetricCardColorPath = (
  mode: MetricCardColorMode,
  role: MetricCardColorRole,
): string => `component.metricCard.${mode}.${role}`

/** Chakra `color` / `bg` / `borderColor` paths on `theme.colors.component.metricCard`. */
export const metricCardColorPaths = {
  dark: {
    label: componentMetricCardColorPath('dark', 'labelColor'),
    value: componentMetricCardColorPath('dark', 'valueColor'),
    border: componentMetricCardColorPath('dark', 'borderColor'),
    background: componentMetricCardColorPath('dark', 'background'),
    separator: componentMetricCardColorPath('dark', 'separatorColor'),
  },
  light: {
    label: componentMetricCardColorPath('light', 'labelColor'),
    value: componentMetricCardColorPath('light', 'valueColor'),
    border: componentMetricCardColorPath('light', 'borderColor'),
    background: componentMetricCardColorPath('light', 'background'),
    separator: componentMetricCardColorPath('light', 'separatorColor'),
  },
} as const

export const metricCardColorModeStyles = {
  dark: {
    labelColor: metricCardColorPaths.dark.label,
    valueColor: metricCardColorPaths.dark.value,
    labelSize: metricCardTypography.dark.label.fontSize,
    valueSize: metricCardTypography.dark.value.fontSize,
    valueWeight: metricCardTypography.dark.value.fontWeight,
    labelWeight: metricCardTypography.dark.label.fontWeight,
    labelLineHeight: metricCardTypography.dark.label.lineHeight,
    valueLineHeight: metricCardTypography.dark.value.lineHeight,
  },
  light: {
    labelColor: metricCardColorPaths.light.label,
    valueColor: metricCardColorPaths.light.value,
    labelSize: metricCardTypography.light.label.fontSize,
    valueSize: metricCardTypography.light.value.fontSize,
    valueWeight: metricCardTypography.light.value.fontWeight,
    labelWeight: metricCardTypography.light.label.fontWeight,
    labelLineHeight: metricCardTypography.light.label.lineHeight,
    valueLineHeight: metricCardTypography.light.value.lineHeight,
  },
} as const

export const metricCardContainerColorModeStyles = {
  dark: {
    borderColor: metricCardColorPaths.dark.border,
    background: metricCardColorPaths.dark.background,
    separatorColor: metricCardColorPaths.dark.separator,
  },
  light: {
    borderColor: metricCardColorPaths.light.border,
    background: metricCardColorPaths.light.background,
    separatorColor: metricCardColorPaths.light.separator,
  },
} as const

/** CMS Detail widget label/value styles for dark metric tiles (Port to Port, etc.). */
export const metricCardDetailWidgetStyles = {
  dark: {
    label: {
      fontSize: metricCardTypography.dark.label.fontSize,
      fontWeight: String(metricCardTypography.dark.label.fontWeight),
      color: metricCardColorPaths.dark.label,
    },
    value: {
      fontSize: metricCardTypography.dark.value.fontSize,
      fontWeight: String(metricCardTypography.dark.value.fontWeight),
      color: metricCardColorPaths.dark.value,
    },
  },
} as const
